import { useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiExternalLink } from 'react-icons/fi';
import SectionHeading from './SectionHeading';
import useReveal from './useReveal';
import palm from '../assets/palm.png';
import bluebest from '../assets/blue2.png';
import baheran from '../assets/baheran.jpg';
import gym from '../assets/gym.jpg';
import milkomi from '../assets/milkomi.png';

const projects = [
  {
    logo: palm,
    short: 'Palm Property',
    name: 'Palm Realestate Property Management App',
    quote:
      'Using Palm Property Manager has made managing our real estate portfolio effortless. This tool has completely transformed our approach to property management. We eased tenant communications, maintenance tracking, and rent collection with its powerful features, user-friendly design, and analytical data. Palm Property Manager has helped us save time and improve our operational effectiveness significantly.',
    author: 'Selamawit Ayalew, General Manager',
  },
  {
    logo: baheran,
    short: 'Baheran Rentals',
    name: 'Baheran Rentals',
    quote:
      "Using Baheran Rentals has completely transformed how we manage our rental properties. The app simplifies every aspect of property management, making it easy to handle tenant interactions, maintenance requests, and rent collection all in one place. Its sleek design and powerful features provide invaluable insights that have significantly improved our efficiency. Since adopting Baheran Rentals, we've gained back precious time and seen a remarkable boost in our overall productivity.",
    author: 'Ato Desse Alemu, Managing Director',
  },
  {
    logo: palm,
    short: 'Palm Socials',
    name: 'Palm Socials',
    quote:
      "Ever since we started using Palm Socials, managing our social media presence has become effortless. This app has revolutionized the way we handle our social media accounts. It offers powerful features, intuitive interface, and excellent analytics that have helped us streamline our content creation, scheduling, and engagement. With Palm Socials, we save valuable time and see noticeable improvements in our social media performance. It's a must-have tool for any business looking to level up their social media game.",
    author: 'Selamawit Ayalew, General Manager',
  },
  {
    logo: milkomi,
    short: 'Milkomi',
    name: 'Milkomi Consultancy Commercial Website',
    quote:
      'Working with Mina Technologies was a game changer for our educational platform. Their expertise in web development and design brought our vision to life, creating a user-friendly and visually appealing website that perfectly reflects our mission. They were attentive, innovative, and always ready to tackle challenges with a solutions-oriented mindset. Thanks to their hard work and dedication, our online presence has significantly improved, allowing us to better serve our clients. I highly recommend their services to anyone looking to enhance their digital experience!',
    author: 'Ato Adnan, General Manager',
  },
  {
    logo: bluebest,
    short: 'BlueBest Trading',
    name: 'BlueBest Trading Commercial Website',
    url: 'https://www.bluebesttrading.com',
    quote:
      "Working with Mina Tech was a game-changer for our business. They designed and developed a stunning commercial website that perfectly represents our brand and effectively showcases our products and services. Their team was attentive, responsive, and truly understood our vision. The website they created has not only attracted more traffic but has also significantly increased our online conversions. We couldn't be happier with the results and highly recommend Mina Tech for anyone looking to elevate their online presence.",
    author: 'Ato Abdulhamid Ali, General Manager',
  },
  {
    logo: gym,
    short: 'SuperStar Gym',
    name: 'SuperStar Gym Management',
    quote:
      "Implementing the system in our gym has been a game-changer. This attendance management system has simplified our operations and enhanced the overall member experience. It's user-friendly, efficient, and allows us to effortlessly track attendance, manage memberships, and generate insightful reports. Our members love the convenience of checking in with just a tap, and our staff appreciates the seamless integration with other gym management processes. This system has truly transformed the way we run our gym, and we highly recommend it to any fitness facility looking to optimize their operation.",
    author: 'Ato Mohammed Hassena, General Manager',
  },
];

function Portfolio() {
  const [current, setCurrent] = useState(0);
  const ref = useReveal();
  const project = projects[current];

  const go = (index) => setCurrent((index + projects.length) % projects.length);

  return (
    <div ref={ref} className="section-pad bg-surface-2">
      <div className="wrap">
        <SectionHeading
          eyebrow="Portfolio"
          title={<>Work our clients <span className="text-gradient">talk about</span></>}
          lede="Property platforms, commercial websites and management systems, in our clients' own words."
        />

        <div className="reveal mt-12 flex flex-wrap gap-2" data-reveal role="tablist" aria-label="Projects">
          {projects.map((item, index) => (
            <button
              key={item.short}
              type="button"
              role="tab"
              aria-selected={index === current}
              onClick={() => go(index)}
              className={`inline-flex items-center gap-2 rounded-full border py-1.5 pl-1.5 pr-4 text-sm font-medium transition-colors ${
                index === current
                  ? 'border-brand bg-brand text-white'
                  : 'border-line bg-white text-ink hover:border-brand hover:text-brand'
              }`}
            >
              <img src={item.logo} alt="" className="h-7 w-7 rounded-full bg-white object-contain" />
              {item.short}
            </button>
          ))}
        </div>

        <div className="reveal card mt-6 overflow-hidden" data-reveal style={{ '--d': '80ms' }}>
          <div className="grid lg:grid-cols-12">
            <div className="glow-dark flex flex-col justify-between gap-8 p-8 lg:col-span-4 lg:p-10">
              <div key={`meta-${current}`} className="fade-in">
                <span className="inline-grid h-16 w-16 place-items-center overflow-hidden rounded-2xl bg-white p-1.5">
                  <img src={project.logo} alt={`${project.short} logo`} className="h-full w-full object-contain" />
                </span>
                <h3 className="display-md mt-6 text-white">{project.name}</h3>
                {project.url && (
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan hover:underline"
                  >
                    Visit website <FiExternalLink />
                  </a>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button type="button" className="btn-on-dark h-11 w-11 px-0" onClick={() => go(current - 1)} aria-label="Previous project">
                  <FiChevronLeft size={20} />
                </button>
                <button type="button" className="btn-on-dark h-11 w-11 px-0" onClick={() => go(current + 1)} aria-label="Next project">
                  <FiChevronRight size={20} />
                </button>
                <span className="ml-2 font-display text-sm text-subtle">
                  {String(current + 1).padStart(2, '0')} / {String(projects.length).padStart(2, '0')}
                </span>
              </div>
            </div>

            <blockquote key={`quote-${current}`} className="fade-in flex flex-col justify-center p-8 lg:col-span-8 lg:p-12">
              <span className="font-display text-6xl leading-none text-brand/25" aria-hidden="true">“</span>
              <p className="-mt-4 text-lg leading-relaxed text-ink">{project.quote}</p>
              <footer className="mt-6 font-semibold text-muted">— {project.author}</footer>
            </blockquote>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Portfolio;
